import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Check, ChevronRight } from 'lucide-react';

import Text from 'components/text';
import { AdminCcRequest, CcRequestOrderBy } from 'hooks/swrRequests/useAdminCcRequests';
import { formatShortDate } from 'utils/helpers';
import type { Artist } from 'types';

import type { SortDirection } from './types';
import { FONT } from './constants';
import ArtistIdentity from './components/artist-identity';
import Badge from './components/badge';
import ArCell from './components/ar-cell';
import PrimaryButton from './components/primary-button';
import TableState from './components/table-state';
import { MiniSortPill, PlainHeaderCell, SortableHeaderCell } from './components/table-header';

const REQUESTS_COL_COUNT = 6;

const StatusPill: React.FC<{ connected: number; total: number }> = ({ connected, total }) => {
  const tone =
    total > 0 && connected >= total
      ? { bg: '#dcfce7', color: '#15803d' }
      : connected > 0
      ? { bg: 'rgba(245,158,11,0.16)', color: '#b45309' }
      : { bg: '#f1f5f9', color: '#64748b' };
  return (
    <Box
      component="span"
      display="inline-flex"
      alignItems="center"
      gap="5px"
      fontSize={FONT.sm}
      fontWeight={600}
      padding="3px 9px"
      borderRadius="999px"
      bgcolor={tone.bg}
      color={tone.color}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {connected > 0 && <Check size={12} />}
      {connected} / {total} connected
    </Box>
  );
};

const RequestRow: React.FC<{
  request: AdminCcRequest;
  onConnect: (request: AdminCcRequest, partner: Artist) => void;
}> = ({ request, onConnect }) => {
  const [expanded, setExpanded] = useState(false);

  const artist = request.fromCcArtist;

  const targets = request.requestArtists;
  const wantsNames = targets
    .map((ra) => ra.ccArtist?.name)
    .filter(Boolean)
    .join(', ');
  const connectedCount = targets.filter((ra) => ra.ccMatchId != null).length;

  return (
    <>
      <TableRow hover>
        {/* Artist (name + folded genre · location · listeners) */}
        <TableCell>
          <ArtistIdentity artist={artist} fallbackName="Unknown artist" />
        </TableCell>

        {/* Wants to collab with — caret toggles the per-target sub-rows */}
        <TableCell sx={{ maxWidth: 320 }}>
          {targets.length > 0 ? (
            <Box
              component="button"
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
              display="inline-flex"
              alignItems="flex-start"
              gap="9px"
              maxWidth="100%"
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                padding: '3px 0',
                fontFamily: 'inherit',
                textAlign: 'left',
              }}
            >
              <ChevronRight
                size={16}
                color="#94a3b8"
                style={{
                  flexShrink: 0,
                  marginTop: 3,
                  transition: 'transform .15s',
                  transform: expanded ? 'rotate(90deg)' : 'none',
                }}
              />
              <Box component="span" color="#0f172a" fontSize={FONT.base}>
                {wantsNames}
              </Box>
            </Box>
          ) : (
            <Text fontSize={FONT.base} color="#94a3b8">
              —
            </Text>
          )}
        </TableCell>

        {/* Status — clicking also expands the per-target sub-rows */}
        <TableCell
          onClick={targets.length > 0 ? () => setExpanded((e) => !e) : undefined}
          sx={targets.length > 0 ? { cursor: 'pointer' } : undefined}
        >
          <StatusPill connected={connectedCount} total={targets.length} />
        </TableCell>

        {/* A&R */}
        <TableCell>
          <ArCell user={request.arUser} />
        </TableCell>

        {/* Date */}
        <TableCell sx={{ color: '#0f172a', fontSize: FONT.base }}>
          {formatShortDate(request.requestDate ?? request.createdAt)}
        </TableCell>

        {/* Source */}
        <TableCell>
          <Badge variant="outline">{request.source === 'manual' ? 'Manual' : 'Dashboard'}</Badge>
        </TableCell>
      </TableRow>

      {/* Per-target sub-rows */}
      {expanded &&
        targets.map((ra) => (
          <TableRow key={ra.id} sx={{ '& > td': { backgroundColor: 'rgba(241,245,249,0.4)' } }}>
            {/* Artist + Wants — name spans both columns for more room */}
            <TableCell colSpan={2} sx={{ padding: '8px 20px 8px 56px' }}>
              <ArtistIdentity
                artist={ra.ccArtist}
                fallbackName="?"
                avatarSize={32}
                nameWeight={500}
                align="center"
              />
            </TableCell>

            {/* Status — aligned under the Status column */}
            <TableCell>
              {ra.ccMatchId != null ? (
                <Badge variant="success">
                  <Check size={12} /> Connected
                </Badge>
              ) : (
                <PrimaryButton
                  variant="contained"
                  size="small"
                  onClick={() => onConnect(request, ra.ccArtist)}
                >
                  Connect
                </PrimaryButton>
              )}
            </TableCell>

            {/* Remaining columns (A&R · Date · Source) */}
            <TableCell colSpan={REQUESTS_COL_COUNT - 3} />
          </TableRow>
        ))}
    </>
  );
};

interface RequestsTableProps {
  requests: AdminCcRequest[];
  isLoading: boolean;
  error: unknown;
  search: string;
  orderBy: CcRequestOrderBy;
  orderDirection: SortDirection;
  onSort: (column?: CcRequestOrderBy) => void;
  onClearSearch: () => void;
  onConnect: (request: AdminCcRequest, partner: Artist) => void;
}

const RequestsTable: React.FC<RequestsTableProps> = ({
  requests,
  isLoading,
  error,
  search,
  orderBy,
  orderDirection,
  onSort,
  onClearSearch,
  onConnect,
}) => {
  if (isLoading || error || requests.length === 0) {
    return (
      <TableState
        isLoading={isLoading}
        error={error}
        isEmpty={requests.length === 0}
        search={search}
        onClearSearch={onClearSearch}
        emptyMessage={search ? `No requests match "${search}"` : 'No collaboration requests yet.'}
        errorMessage="Couldn't load requests. Please try again."
      />
    );
  }

  return (
    <TableContainer>
      <Table sx={{ tableLayout: 'fixed', minWidth: 800 }}>
        {/* Proportional column widths: Artist | Wants | Status | A&R | Date | Source */}
        <colgroup>
          <col style={{ width: '25%' }} />
          <col style={{ width: '24%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '12%' }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: FONT.base, fontWeight: 500, color: '#64748b' }}>
              <Box
                component="span"
                display="inline-flex"
                alignItems="center"
                flexWrap="wrap"
                gap="8px"
              >
                <Box
                  component="span"
                  fontSize={FONT.sm}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                  Artist
                </Box>
                <MiniSortPill
                  label="Name"
                  active={orderBy === 'name'}
                  direction={orderDirection}
                  onClick={() => onSort('name')}
                />
                <MiniSortPill
                  label="Listeners"
                  active={orderBy === 'monthlyListeners'}
                  direction={orderDirection}
                  onClick={() => onSort('monthlyListeners')}
                />
                <MiniSortPill
                  label="Location"
                  active={orderBy === 'location'}
                  direction={orderDirection}
                  onClick={() => onSort('location')}
                />
              </Box>
            </TableCell>
            <PlainHeaderCell label="Wants to collab with" />
            <PlainHeaderCell label="Status" />
            <SortableHeaderCell
              label="A&R"
              active={orderBy === 'arUser'}
              direction={orderDirection}
              onClick={() => onSort('arUser')}
            />
            <SortableHeaderCell
              label="Date"
              active={orderBy === 'requestDate'}
              direction={orderDirection}
              onClick={() => onSort('requestDate')}
            />
            <PlainHeaderCell label="Source" />
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <RequestRow key={request.id} request={request} onConnect={onConnect} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RequestsTable;
